import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditTextareaField } from '../../../EditTextareaField';
import type { ContactOtherFieldsData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        social
        nextStepsPlugin
      }
    }
  }
`;

const NOTES_FIELDS: Record<string, { apiField: string }> = {
  nextStepsPlugin: { apiField: 'nextStepsPlugin' },
  social: { apiField: 'social' }
};

export interface EditNotesDialogProps {
  contactId: string;
  initialValue: ContactOtherFieldsData;
  isOpen: boolean;
  onChange: (data: ContactOtherFieldsData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactOtherFieldsData;
}

export function EditNotesDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditNotesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactOtherFieldsData>(
    field: K,
    fieldValue: ContactOtherFieldsData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save Notes');
      } else {
        toast.success('Notes saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving Notes:', error);
      toast.error('Failed to save Notes');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Notes"
    >
      <div className="space-y-6">
        <EditTextareaField
          label="Social"
          onChange={v => {
            updateField('social', v);
          }}
          rows={3}
          value={value.social}
        />
        <EditTextareaField
          label="Next Steps Plugin"
          onChange={v => {
            updateField('nextStepsPlugin', v);
          }}
          rows={3}
          value={value.nextStepsPlugin}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactOtherFieldsData,
  initial: ContactOtherFieldsData
): Record<string, null | string> {
  const changes: Record<string, null | string> = {};

  for (const [uiField, config] of Object.entries(NOTES_FIELDS)) {
    const currentVal = current[uiField as keyof ContactOtherFieldsData];
    const initialVal = initial[uiField as keyof ContactOtherFieldsData];

    if (currentVal !== initialVal) {
      const strVal = currentVal as null | string;
      changes[config.apiField] = strVal === '' ? null : (strVal ?? null);
    }
  }

  return changes;
}
